import React, { forwardRef, useCallback, useEffect, useMemo } from "react";
import { defaultMemoize } from "reselect";

import { ControlReduxRawValue, ExplicitValidation, InstantValidation } from "@multiweb/reactControls";
import { combineRefs, EMPTY_ARRAY } from "@multiweb/reactUtils";
import { Cursor } from "@multiweb/types/multiweb";
import { ForwardRefType } from "@multiweb/types/react";
import { ControlValueWrapper } from "../ControlValueWrapper";
import { FormComponentPropsWrapper } from "../FormComponentPropsWrapper";
import { InputFake } from "../InputFake";
import { MultiEditWrapper, MultiEditWrapperFakeComponentRendererProps } from "../MultiEditWrapper";
import { PasswordEditComponent } from "../PasswordEditComponent";
import { DataAttributesProps, EditableControlComponentProps } from "../types";
import { ValidationErrorWrapper } from "../ValidationErrorWrapper";

export type PasswordEditProps = {
    /** Příznak, zda-li se má zobrazit tlačítko pro odkrytí hesla */
    showUnhideButton?: boolean;
    /** Hodnota kurzoru předávaná níže do tlačítka */
    cursor?: Cursor;
}
& Omit<EditableControlComponentProps, "ref">
& Pick<DataAttributesProps, "maxLength" | "validationRegex">;

export const PasswordEdit: ForwardRefType<HTMLInputElement, PasswordEditProps> = forwardRef<HTMLInputElement, PasswordEditProps>(({
    id,
    dataKey,
    maxLength,
    validationRegex,
    externalErrors,
    isInRequiredInvalidState,
    onValidationCallback,
    onValidityChanged,
    onBlur,
    onChange: onChangeExternal,
    onMount,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    onMouseOver,
    onKeyDown,
    onClick,
    triggerValueChange,
    locKey,
    encrypted = false,
    multiEditActive,
    touched,
    enabledVisual,
    recommended,
    required = false,
    postponedRequired,
    isEditing,
    showUnhideButton = true,
    cursor = Cursor.initial,
}, ref) => {
    // useMemo vrací memoizovanou funkci, která je přepočítána jenom v případě, kdy se mění parametr maxLength nebo rawValue.
    // Pokud jsou obě hodnoty stejné jako při posledním volání, nic nepřepočítává a vrací předchozí funkci.
    const createInstantDesc = useMemo(() => defaultMemoize((maxLength: number | undefined, rawValue: ControlReduxRawValue | undefined) =>
        maxLength ? [InstantValidation.createLengthDescription(rawValue, maxLength)] : EMPTY_ARRAY,
    ), []);

    const explicitValidationFnDescriptions = useMemo(() => validationRegex ? [ExplicitValidation.createRegexDescription(validationRegex)] : EMPTY_ARRAY, [validationRegex]);

    useEffect(() => {
        onMount && onMount({
            getDisplayedValue: (rawValue) => rawValue,
            getControlValue: (rawValue) => rawValue,
            convertControlValueToRawValue: (controlValue) => controlValue,
        });
        // viz koment dole, bude opraveno v ramci 30853
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fakeComponentRenderer = useCallback(({ disabled }: MultiEditWrapperFakeComponentRendererProps) => (
        <InputFake { ...{ disabled, isEditing, locKey, onKeyDown, onClick, required, recommended, postponedRequired } } />
    ), [isEditing, locKey, onKeyDown, onClick, required, recommended, postponedRequired]);

    return (
        <ControlValueWrapper dataKey={dataKey}>
            {({ rawValue, onChange }) => {
                const instantValidationFnDescriptions = createInstantDesc(maxLength, rawValue);
                return (
                    <ValidationErrorWrapper {...{ dataKey, instantValidationFnDescriptions, explicitValidationFnDescriptions, externalErrors, isInRequiredInvalidState, onValidityChanged }}>
                        {({ invalid, validate }) => (
                            <FormComponentPropsWrapper {...{ postponedRequired, required, recommended, enabledVisual }}>
                                {({ disabled, backgroundColor, readOnly }) => (
                                    <MultiEditWrapper fakeComponentRenderer={fakeComponentRenderer} {...{ multiEditActive, disabled, isEditing, touched }}>
                                        {({ ref: editRef }) => {
                                            const joinedRefs = combineRefs<HTMLInputElement>(editRef, ref);
                                            return (
                                                <PasswordEditComponent
                                                    {...{
                                                        id,
                                                        onFocus,
                                                        onMouseEnter,
                                                        onMouseLeave,
                                                        onMouseOver,
                                                        onClick,
                                                        encrypted,
                                                        required,
                                                        showUnhideButton,
                                                        disabled,
                                                        invalid,
                                                        backgroundColor,
                                                        readOnly,
                                                        cursor,
                                                        value: rawValue,
                                                        ref: joinedRefs,
                                                    }}
                                                    onBlur={(e) => {
                                                        validate(onValidationCallback);
                                                        onBlur && onBlur(e);
                                                    }}
                                                    onChange={(e) => {
                                                        onChange(e);
                                                        onChangeExternal && onChangeExternal(e);
                                                    }}
                                                    onJavaScriptChange={(_e) => {
                                                        triggerValueChange && triggerValueChange();
                                                    }}
                                                />
                                            );
                                        }
                                        }
                                    </MultiEditWrapper>
                                )}
                            </FormComponentPropsWrapper>
                        )}
                    </ValidationErrorWrapper>
                );
            }
            }
        </ControlValueWrapper>
    );
});
